import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';

export type Currency = 'BRL' | 'USD' | 'EUR';

interface ExchangeRates {
  USD_BRL: number;
  EUR_USD: number;
  EUR_BRL: number;
}

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  exchangeRates: ExchangeRates;
  ratesUpdatedAt: number | null;
  isLoading: boolean;
  formatValue: (value: number | null | undefined, sourceCurrency?: string) => string;
  convertValue: (value: number | null | undefined, sourceCurrency?: string) => number | null;
}

const DEFAULT_RATES: ExchangeRates = {
  USD_BRL: 6.00,
  EUR_USD: 1.08,
  EUR_BRL: 6.50,
};

const CURRENCY_LABELS: Record<Currency, { flag: string; name: string; symbol: string }> = {
  BRL: { flag: '🇧🇷', name: 'Real Brasileiro', symbol: 'R$' },
  USD: { flag: '🇺🇸', name: 'Dólar Americano', symbol: 'US$' },
  EUR: { flag: '🇪🇺', name: 'Euro', symbol: '€' },
};

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const CURRENCY_OPTIONS = CURRENCY_LABELS;

export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
  const [currency, setCurrencyState] = useState<Currency>('BRL');
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates>(DEFAULT_RATES);
  const [ratesUpdatedAt, setRatesUpdatedAt] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load preference from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('preferred_currency') as Currency;
    if (stored && ['BRL', 'USD', 'EUR'].includes(stored)) {
      setCurrencyState(stored);
    }
  }, []);

  // Fetch exchange rates from AwesomeAPI (single request for all pairs)
  useEffect(() => {
    let isMounted = true;
    const CACHE_KEY = 'exchange_rates_cache';
    const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

    function loadCachedRates(): { rates: ExchangeRates; timestamp: number } | null {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (!cached) return null;
        const { rates, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL) return { rates, timestamp };
      } catch { /* ignore */ }
      return null;
    }

    function saveCachedRates(rates: ExchangeRates) {
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ rates, timestamp: Date.now() }));
      } catch { /* ignore */ }
    }

    async function fetchRates() {
      // Try cache first
      const cached = loadCachedRates();
      if (cached) {
        if (isMounted) {
          setExchangeRates(cached.rates);
          setRatesUpdatedAt(cached.timestamp);
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      try {
        // Single request for all pairs
        const res = await fetch('https://economia.awesomeapi.com.br/json/last/USD-BRL,EUR-USD,EUR-BRL');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (isMounted) {
          const rates: ExchangeRates = {
            USD_BRL: data?.USDBRL?.bid ? parseFloat(data.USDBRL.bid) : DEFAULT_RATES.USD_BRL,
            EUR_USD: data?.EURUSD?.bid ? parseFloat(data.EURUSD.bid) : DEFAULT_RATES.EUR_USD,
            EUR_BRL: data?.EURBRL?.bid ? parseFloat(data.EURBRL.bid) : DEFAULT_RATES.EUR_BRL,
          };
          setExchangeRates(rates);
          setRatesUpdatedAt(Date.now());
          saveCachedRates(rates);
        }
      } catch {
        // Keep default or cached rates silently
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    fetchRates();

    // Refresh rates every 30 minutes
    const interval = setInterval(fetchRates, 30 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  const setCurrency = useCallback((newCurrency: Currency) => {
    setCurrencyState(newCurrency);
    localStorage.setItem('preferred_currency', newCurrency);
  }, []);

  // Convert a value from source currency to user's preferred currency
  const convertValue = useCallback((
    value: number | null | undefined,
    sourceCurrency: string = 'USD'
  ): number | null => {
    if (value === null || value === undefined) return null;

    const source = sourceCurrency.toUpperCase();
    const target = currency;

    // No conversion needed
    if (source === target) return value;

    // Conversion logic
    switch (`${source}_${target}`) {
      // From USD
      case 'USD_BRL':
        return value * exchangeRates.USD_BRL;
      case 'USD_EUR':
        return value / exchangeRates.EUR_USD;
      
      // From BRL
      case 'BRL_USD':
        return value / exchangeRates.USD_BRL;
      case 'BRL_EUR':
        return value / exchangeRates.EUR_BRL;
      
      // From EUR
      case 'EUR_USD':
        return value * exchangeRates.EUR_USD;
      case 'EUR_BRL':
        return value * exchangeRates.EUR_BRL;
      
      default:
        // Unknown conversion, return as-is
        return value;
    }
  }, [currency, exchangeRates]);

  // Format a value in the user's preferred currency
  const formatValue = useCallback((
    value: number | null | undefined,
    sourceCurrency: string = 'USD'
  ): string => {
    const converted = convertValue(value, sourceCurrency);
    
    if (converted === null) return '—';

    const locale = currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'de-DE' : 'en-US';
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(converted);
  }, [currency, convertValue]);

  const contextValue = useMemo(() => ({
    currency,
    setCurrency,
    exchangeRates,
    ratesUpdatedAt,
    isLoading,
    formatValue,
    convertValue,
  }), [currency, setCurrency, exchangeRates, ratesUpdatedAt, isLoading, formatValue, convertValue]);

  return (
    <CurrencyContext.Provider value={contextValue}>
      {children}
    </CurrencyContext.Provider>
  );
};

// Fallback for when context is not available
const fallbackContext: CurrencyContextType = {
  currency: 'BRL',
  setCurrency: () => {},
  exchangeRates: DEFAULT_RATES,
  ratesUpdatedAt: null,
  isLoading: false,
  formatValue: (value) => value !== null && value !== undefined ? `R$ ${value.toLocaleString('pt-BR')}` : '—',
  convertValue: (value) => value ?? null,
};

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  
  if (!context) {
    console.warn('useCurrency called outside CurrencyProvider, using fallback');
    return fallbackContext;
  }
  
  return context;
};
